"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StepperItem = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const MoveToViewport_1 = require("@jsonjoy.com/ui/lib/utils/popup/MoveToViewport");
const PopupControlled_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/PopupControlled");
const context_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/context");
const popup_1 = require("@jsonjoy.com/ui/lib/utils/popup");
const useLockScrolling_1 = require("@jsonjoy.com/ui/lib/hooks/useLockScrolling");
const useSingletonPopup_1 = require("@jsonjoy.com/ui/lib/hooks/useSingletonPopup");
const context_2 = require("@jsonjoy.com/ui/lib/styles/context");
const Scrollbox_1 = require("@jsonjoy.com/ui/lib/4-card/Scrollbox");
const Bullet_1 = require("./Bullet");
const BulletOptions_1 = require("./BulletOptions");
const Connector_1 = require("./Connector");
const StepBody_1 = require("./StepBody");
const colors_1 = require("./colors");
const settings_1 = require("./settings");
const font_1 = require("../../behavior/font");
const itemClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    listStyle: 'none',
    d: 'flex',
    fld: 'row',
    ai: 'stretch',
    gap: '12px',
    pad: `0 0 ${settings_1.ITEM_GAP}px 0`,
    '&:last-child': {
        pad: '0',
    },
});
const indicatorColClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'block',
    fls: '0 0 auto',
    w: `${settings_1.INDICATOR_SIZE}px`,
    minH: `${settings_1.INDICATOR_SIZE + 8}px`,
});
const triggerClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'inline-block',
    pad: 0,
    bd: 'none',
    bg: 'transparent',
    cur: 'pointer',
    w: `${settings_1.INDICATOR_SIZE}px`,
    h: `${settings_1.INDICATOR_SIZE}px`,
    lh: '0',
});
const indexInParent = (path) => path[path.length - 1] ?? 0;
const StepperItem = ({ attributes, children, element }) => {
    const editor = (0, slate_react_1.useSlateStatic)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const styles = (0, context_2.useStyles)();
    const popup = (0, useSingletonPopup_1.useSingletonPopup)('stepper-bullet');
    const closePopup = React.useCallback(() => popup.setOpen(false), [popup]);
    const popupContextValue = React.useMemo(() => ({ close: closePopup }), [closePopup]);
    const popupAnchorHandle = (0, popup_1.useAnchorPointHandle)({
        horizontal: true,
        center: true,
        pinX: 'left',
        minSpace: 360,
        gap: 8,
    });
    (0, useLockScrolling_1.useLockScrolling)(popup.open);
    let index = 0;
    let isLast = false;
    try {
        const path = slate_react_1.ReactEditor.findPath(editor, element);
        index = indexInParent(path);
        const [parent] = slate_1.Editor.parent(editor, path);
        if (slate_1.Element.isElement(parent))
            isLast = index === parent.children.length - 1;
    }
    catch { }
    const state = (0, settings_1.getStepState)(element.stepState);
    const indicator = (0, settings_1.getStepIndicator)(element.stepIndicator);
    const stateColors = (0, colors_1.getStepStateColors)(styles, state);
    // Optional state hides the ring by default — the dashed-circle glyph
    // already reads as a ring, so a solid outline would visually compete.
    const stateRingDefault = state === 'optional' ? 'none' : settings_1.DEF_RING;
    const ring = (0, settings_1.getLineStyle)(element.ring, stateRingDefault);
    const ringColor = element.ringCol ?? stateColors.line;
    const ringWidth = element.ringWidth ?? settings_1.DEF_RING_WIDTH;
    const halo = (0, settings_1.getLineStyle)(element.halo, settings_1.DEF_HALO);
    const haloColor = element.haloCol ?? stateColors.line;
    const haloWidth = element.haloWidth ?? settings_1.DEF_HALO_WIDTH;
    const line = (0, settings_1.getLineStyle)(element.line, settings_1.DEF_LINE);
    const lineColor = element.lineCol ?? stateColors.line;
    const lineWidth = element.lineWidth ?? settings_1.DEF_LINE_WIDTH;
    const bg = element.stepBg ?? stateColors.bg;
    const autoGlyph = element.stepBg ? (0, colors_1.pickGlyphColor)(styles, bg, stateColors.glyph) : stateColors.glyph;
    const glyphColor = element.stepCol ?? autoGlyph;
    const haloDrawn = halo !== 'none' && haloWidth > 0;
    const connectorTop = settings_1.INDICATOR_SIZE + (haloDrawn ? 10 : 4);
    const preventMouseDown = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);
    const bullet = (React.createElement(Bullet_1.Bullet, { state: state, indicator: indicator, chars: element.stepChar ?? '', index: index, bg: bg, glyphColor: glyphColor, ring: ring, ringColor: ringColor, ringWidth: ringWidth, halo: halo, haloColor: haloColor, haloWidth: haloWidth }));
    const ariaState = state === 'done'
        ? 'completed'
        : state === 'error'
            ? 'errored'
            : state === 'warning'
                ? 'warning'
                : state === 'optional'
                    ? 'optional'
                    : state === 'pending'
                        ? 'pending'
                        : 'active';
    return (React.createElement("li", { ...attributes, className: `${itemClass} ${StepBody_1.stepHoverStripBarClass}`, "data-step-state": state, "aria-current": state === 'active' ? 'step' : undefined, "aria-label": `Step ${index + 1}, ${ariaState}`, style: {
            textAlign: element.align,
            fontFamily: (0, font_1.fontFamilyOf)(element.font),
            ['--mutxt-handle-color']: styles.g(0.72),
            ['--mutxt-handle-color-hover']: styles.g(0.35),
        } },
        React.createElement("span", { className: indicatorColClass, contentEditable: false },
            readOnly ? (React.createElement("span", { className: triggerClass }, bullet)) : (React.createElement(context_1.context.Provider, { value: popupContextValue },
                React.createElement(popup_1.anchorContext.Provider, { value: popupAnchorHandle },
                    React.createElement(PopupControlled_1.PopupControlled, { open: popup.open, refToggle: popupAnchorHandle.ref, onHeadClick: () => popup.setOpen(!popup.open), onClickAway: closePopup, onEsc: popup.open ? closePopup : undefined, renderContext: () => (React.createElement(MoveToViewport_1.MoveToViewport, { vertical: true },
                            React.createElement("div", { style: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: 'calc(100vh - 16px)',
                                } },
                                React.createElement(Scrollbox_1.Scrollbox, { shadow: true },
                                    React.createElement("div", { style: { padding: 16 } },
                                        React.createElement(BulletOptions_1.BulletOptions, { element: element })))))) },
                        React.createElement("button", { type: "button", className: triggerClass, "aria-label": `Step ${index + 1} options, currently ${ariaState}`, "aria-haspopup": "dialog", "aria-expanded": popup.open, onMouseDown: preventMouseDown }, bullet))))),
            !isLast && React.createElement(Connector_1.Connector, { style: line, color: lineColor, width: lineWidth, top: connectorTop })),
        React.createElement(StepBody_1.StepBody, { element: element, state: state }, children)));
};
exports.StepperItem = StepperItem;
